import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { apiClient as defaultClient, type ApiClient, type ScanObservation } from '../api/client';
import { AppShell } from '../components/AppShell';
import { Card } from '../components/Card';
import { Spinner } from '../components/Spinner';
import { InlineAlert } from '../components/InlineAlert';
import { StatusBadge } from '../components/StatusBadge';
import { useOrg } from '../org/context';
import { useFocusMain } from '../a11y/useFocusMain';
import { toDisplayError } from '../api/errorMessages';
import { AuthedBrand } from './AuthedBrand';
import styles from './ScanDiscovery.module.css';

const POLL_INTERVAL_MS = 4000;
const ANNOUNCE_THROTTLE_MS = 15_000;
const PAGE_LIMIT = 100;
const TERMINAL = new Set(['completed', 'failed', 'cancelled']);

/**
 * The empty-observations copy, honest about WHY there are none. A `failed` scan
 * must never read as a successful "Scan completed" (#436), and a seed blocked by
 * bot protection gets an actionable, non-self-blaming explanation (#431).
 */
function emptyObservationsMessage(
  status: string,
  running: boolean,
  failureReason: string | null,
): string {
  if (running) return 'No observations yet. Discovery is still running.';
  if (status === 'failed') {
    if (failureReason === 'seed_blocked_by_bot_protection') {
      return 'This storefront is behind bot protection (e.g. Cloudflare) that blocks automated browsers, so the scan couldn’t reach it. For a real storefront, allowlist TestCraft’s crawler; some demo sites can’t be scanned.';
    }
    return 'Scan failed before any observations were collected — we couldn’t reach the site. Some sites block automated access.';
  }
  if (status === 'cancelled') return 'Scan cancelled before any observations were collected.';
  return 'Scan completed. No observations were found.';
}

export interface ScanDiscoveryProps {
  client?: ApiClient;
  /** Poll cadence override (tests). */
  pollIntervalMs?: number;
}

type Phase = 'loading' | 'error' | 'ready';

/**
 * Progressive discovery results for one scan (Story #19, FR-2.5). While the scan
 * is non-terminal, polls the observations endpoint, re-draining all pages each
 * cycle and upserting by id (so a pending observation surfaces its classification
 * on a later poll). Polling stops only on a TERMINAL status — never on cursor
 * exhaustion. These are CANDIDATE observations from live discovery, NOT validated
 * or materialized journeys (that is #20). A single always-mounted polite live
 * region announces a throttled count; the list itself is not a live region.
 */
export function ScanDiscovery({
  client = defaultClient,
  pollIntervalMs = POLL_INTERVAL_MS,
}: ScanDiscoveryProps) {
  const { currentOrg } = useOrg();
  const { id, scanId } = useParams<{ id: string; scanId: string }>();
  useFocusMain();
  const orgId = currentOrg?.id;

  const [phase, setPhase] = useState<Phase>('loading');
  const [loadMessage, setLoadMessage] = useState('');
  const [scanStatus, setScanStatus] = useState<string>('');
  const [failureReason, setFailureReason] = useState<string | null>(null);
  const [items, setItems] = useState<ScanObservation[]>([]);
  const [announcement, setAnnouncement] = useState('');

  const loadedRef = useRef(false);
  const lastAnnounceAtRef = useRef(0);

  useEffect(() => {
    if (!orgId || !id || !scanId) return;
    let cancelled = false;
    let timer: number | undefined;

    const announce = (count: number, status: string) => {
      const terminal = TERMINAL.has(status);
      const now = Date.now();
      if (!terminal && now - lastAnnounceAtRef.current < ANNOUNCE_THROTTLE_MS && lastAnnounceAtRef.current !== 0) {
        return;
      }
      lastAnnounceAtRef.current = now;
      const tail = ' Candidate observations only; not validated journeys.';
      setAnnouncement(
        terminal
          ? `Discovery ${status}. ${count} observation${count === 1 ? '' : 's'} discovered.${tail}`
          : `${count} observation${count === 1 ? '' : 's'} discovered.${tail}`,
      );
    };

    const poll = async () => {
      try {
        const byId = new Map<string, ScanObservation>();
        const order: string[] = [];
        let cursor: string | undefined;
        let status = '';
        let failReason: string | null = null;
        for (let guard = 0; guard < 500; guard += 1) {
          const page = await client.getScanObservations(
            orgId,
            id,
            scanId,
            cursor ? { limit: PAGE_LIMIT, cursor } : { limit: PAGE_LIMIT },
          );
          status = page.scanStatus;
          failReason = page.failureReason;
          for (const obs of page.items) {
            if (!byId.has(obs.id)) order.push(obs.id);
            byId.set(obs.id, obs);
          }
          if (!page.nextCursor) break;
          cursor = page.nextCursor;
        }
        if (cancelled) return;
        loadedRef.current = true;
        setScanStatus(status);
        setFailureReason(failReason);
        setItems(order.map((oid) => byId.get(oid) as ScanObservation));
        setPhase('ready');
        announce(order.length, status);
        if (TERMINAL.has(status) && timer !== undefined) {
          window.clearInterval(timer);
          timer = undefined;
        }
      } catch (err) {
        if (cancelled) return;
        // Only the FIRST load surfaces an error; later poll failures keep the
        // last results (transient — resume next tick).
        if (!loadedRef.current) {
          setLoadMessage(toDisplayError(err).message);
          setPhase('error');
        }
      }
    };

    void poll();
    timer = window.setInterval(() => void poll(), pollIntervalMs);
    return () => {
      cancelled = true;
      if (timer !== undefined) window.clearInterval(timer);
    };
  }, [orgId, id, scanId, pollIntervalMs, client]);

  const running = !TERMINAL.has(scanStatus);

  return (
    <AppShell brand={<AuthedBrand />}>
      <main className={styles.main}>
        <Link to={`/projects/${id ?? ''}/scans`} className={styles.backLink}>
          ← Back to scans
        </Link>
        <h1 className={styles.heading}>Live discovery</h1>
        <p className={styles.caveat}>
          Candidate observations from live discovery — <strong>not</strong> validated or
          materialized journeys (see journey health once the scan completes).
        </p>

        {/* Single always-mounted polite live region (announcements only). */}
        <InlineAlert severity="info">{announcement || undefined}</InlineAlert>

        {phase === 'loading' && (
          <div className={styles.center}>
            <Spinner />
            <span>Loading live discovery…</span>
          </div>
        )}

        {phase === 'error' && (
          <InlineAlert severity="error">{loadMessage}</InlineAlert>
        )}

        {phase === 'ready' && (
          <Card>
            <div className={styles.summary}>
              <StatusBadge kind="scan" status={scanStatus} />
              <span className={styles.count}>
                {items.length} discovered
                {running ? ' · discovery in progress' : ''}
              </span>
            </div>

            {items.length === 0 ? (
              <p className={styles.empty}>
                {emptyObservationsMessage(scanStatus, running, failureReason)}
              </p>
            ) : (
              <ul className={styles.list}>
                {items.map((obs) => {
                  const types = obs.classification?.journeyTypes ?? [];
                  const pending = obs.classification === null;
                  return (
                    <li
                      key={obs.id}
                      className={pending ? `${styles.item} ${styles.pending}` : styles.item}
                    >
                      <span className={styles.url}>{obs.url}</span>
                      <span className={styles.labels}>
                        {pending ? (
                          <span className={styles.classifying}>Classifying candidate type…</span>
                        ) : types.length === 0 ? (
                          <span className={styles.classifying}>No candidate type</span>
                        ) : (
                          types.map((t) => (
                            <span key={t} className={styles.chip}>
                              Candidate: {t}
                            </span>
                          ))
                        )}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </Card>
        )}
      </main>
    </AppShell>
  );
}
